import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Trash2, Eye, Play } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface DeleteResult {
  scope: string;
  tableName: string;
  deletedCount: number;
  error?: string;
}

interface DeleteResponse {
  success: boolean;
  dryRun: boolean;
  totalDeleted: number;
  results: DeleteResult[];
  message: string;
  error?: string;
  hasMore?: boolean;
}

const DATA_SCOPES = [
  {
    id: 'crawl_history',
    label: 'Crawl History',
    description: 'All TradingView crawler jobs and their results',
    tables: ['crawl_jobs']
  },
  {
    id: 'market_analysis',
    label: 'Market Analysis Results',
    description: 'AI analysis sessions, steps, cards, and performance data',
    tables: ['ai_analysis_sessions', 'ai_analysis_steps', 'ai_analysis_cards', 'ai_analysis_performance']
  },
  {
    id: 'trading_patterns',
    label: 'Trading Pattern Analysis',
    description: 'Pattern analysis results, predictions, and backups',
    tables: ['trading_analysis_results', 'trading_patterns', 'pattern_predictions', 'pattern_backups']
  },
  {
    id: 'wlls_data',
    label: 'WLLS Data',
    description: 'Win/Loss/Long/Short analysis files and patterns',
    tables: ['wlls_files', 'wlls_patterns']
  },
  {
    id: 'tradingview_signals',
    label: 'TradingView Signals',
    description: 'All collected TradingView signal data',
    tables: ['tradingview_signals']
  }
];

export const DataResetDangerZone = () => {
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<DeleteResult[]>([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [currentOperation, setCurrentOperation] = useState('');

  const handleScopeToggle = (scopeId: string) => {
    setSelectedScopes(prev => 
      prev.includes(scopeId) 
        ? prev.filter(id => id !== scopeId)
        : [...prev, scopeId]
    );
  };

  const executeBatchedDataReset = async (dryRun: boolean = false) => {
    if (!selectedScopes.length) {
      toast.error('Please select at least one data scope');
      return;
    }

    setIsLoading(true);
    setResults([]);
    setBatchProgress({ current: 0, total: 0 });

    let allResults: DeleteResult[] = [];
    let batchCount = 0;
    let hasMoreData = true;

    try {
      while (hasMoreData && batchCount < 100) { // Safety limit
        batchCount++;
        setCurrentOperation(`Processing batch ${batchCount}...`);
        setBatchProgress(prev => ({ ...prev, current: batchCount }));

        const requestBody = {
          scopes: selectedScopes,
          dryRun,
          confirmationToken: dryRun ? undefined : 'CONFIRM_DELETE_ALL_DATA',
          batchSize: 2000
        };

        const { data, error } = await supabase.functions.invoke('admin-data-reset', {
          body: requestBody
        });

        if (error) throw error;

        const response: DeleteResponse = data;
        
        if (!response.success) {
          throw new Error(response.error || 'Unknown error occurred');
        }

        // Merge results
        response.results.forEach(newResult => {
          const existingIndex = allResults.findIndex(
            r => r.scope === newResult.scope && r.tableName === newResult.tableName
          );
          
          if (existingIndex >= 0) {
            allResults[existingIndex].deletedCount += newResult.deletedCount;
            if (newResult.error) {
              allResults[existingIndex].error = newResult.error;
            }
          } else {
            allResults.push(newResult);
          }
        });

        hasMoreData = response.hasMore || false;
        setResults([...allResults]);

        // Show progress message
        const totalDeleted = allResults.reduce((sum, result) => sum + result.deletedCount, 0);
        if (hasMoreData) {
          toast.info(`Batch ${batchCount} complete: ${totalDeleted} records deleted so far...`);
        } else {
          toast.success(`All batches complete: ${totalDeleted} total records ${dryRun ? 'would be deleted' : 'deleted'}`);
        }
      }

      if (batchCount >= 100) {
        toast.warning('Reached maximum batch limit. Some data may remain.');
      }

    } catch (error) {
      console.error('Data reset error:', error);
      toast.error(`Failed to ${dryRun ? 'preview' : 'execute'} data reset: ${error.message}`);
    } finally {
      setIsLoading(false);
      setCurrentOperation('');
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const totalRecords = results.reduce((sum, result) => sum + result.deletedCount, 0);
  const hasErrors = results.some(result => result.error);

  return (
    <Card className="w-full border-destructive/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          Danger Zone - Data Reset
        </CardTitle>
        <CardDescription>
          Permanently delete historical data to clean up the system. This action cannot be undone.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Warning:</strong> This will permanently delete data from the database. 
            Always run a dry-run preview first to see what will be deleted.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <h4 className="font-medium">Select data to delete:</h4>
          
          <div className="grid gap-4">
            {DATA_SCOPES.map((scope) => (
              <div key={scope.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                <Checkbox
                  id={scope.id}
                  checked={selectedScopes.includes(scope.id)}
                  onCheckedChange={() => handleScopeToggle(scope.id)}
                />
                <div className="flex-1">
                  <Label htmlFor={scope.id} className="font-medium cursor-pointer">
                    {scope.label}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {scope.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tables: {scope.tables.join(', ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => executeBatchedDataReset(true)}
            disabled={isLoading || !selectedScopes.length}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            {isLoading ? 'Previewing...' : 'Dry Run Preview'}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                disabled={isLoading || !selectedScopes.length}
                variant="destructive"
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Data Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the selected data from the database. 
                  This action cannot be undone.
                  <br /><br />
                  Selected scopes: <strong>{selectedScopes.join(', ')}</strong>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => executeBatchedDataReset(false)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete Permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {isLoading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Play className="h-4 w-4 animate-pulse" />
              <span className="text-sm">
                {currentOperation || 'Processing data reset...'}
              </span>
            </div>
            {batchProgress.current > 0 && (
              <div className="text-xs text-muted-foreground">
                Batch {batchProgress.current} in progress...
              </div>
            )}
            <Progress value={undefined} className="w-full" />
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Results:</h4>
              <div className="text-sm text-muted-foreground">
                Total: {totalRecords} records
                {hasErrors && <span className="text-destructive ml-2">(with errors)</span>}
              </div>
            </div>

            <div className="space-y-2">
              {results.map((result, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                  <div>
                    <span className="font-medium">{result.tableName}</span>
                    <span className="text-sm text-muted-foreground ml-2">({result.scope})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.error ? (
                      <span className="text-destructive text-sm">Error: {result.error}</span>
                    ) : (
                      <span className="text-sm">{result.deletedCount} records</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};